import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Platform, TouchableOpacity, View, ViewStyle } from 'react-native'

import { isToday } from 'date-fns'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  EssentialsMessageMetadata,
  MESSAGE_AUTHOR_TYPE,
} from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { ChatBubble } from '../../atoms/chatBubble/ChatBubble'
import { ChatBubbleText } from '../../atoms/chatBubble/ChatBubbleText'
import { DeletedLiveMessageText } from '../../atoms/deletedLiveMessageText/DeletedLiveMessageText'
import { AuroraAICoachAvatarIcon } from '../../atoms/icons/AuroraAICoachAvatarIcon'
import { Avatar, avatarAltTextGeneric } from '../../atoms/icons/Avatar'
import { CalendarBookingIcon } from '../../atoms/icons/CalendarBookingIcon'
import { WarningIcon } from '../../atoms/icons/WarningIcon'
import { ExerciseIcon } from '../../atoms/illustrations/ExerciseIcon'
import { Timestamp } from '../../atoms/timestamp/Timestamp'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { IS_WEB } from '../../constants'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { ActivityBubble } from '../../molecules/activityBubble/ActivityBubble'
import { ActivityReviewInfo } from '../../ui-models/assignments/Assignments'
import { Message, MessageAttachmentData, MessageType } from '../../ui-models/messages/Messages'
import { getFriendlyFormattedTimeStamp } from '../../utils/getFriendlyFormattedTimeStamp'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { decodeHtmlEntities, tID } from '../../utils/utils'
import { EssentialsBubble, EssentialsPressInfo } from '../essentialsBubble/EssentialsBubble'
import { MessageAttachmentRenderer } from '../messageAttachmentRenderer/MessageAttachmentRenderer'
import { MessageFeedback } from '../messageFeedback/MessageFeedback'

export type OnAiCoachFeedbackPressedType = (params: {
  value: string | null
  genAiMessageId: string
  feedbackResponseId?: string
}) => Promise<void>

export type MessageItemProps = {
  item: Message
  onChatBubblePressed?: (messageId: string, receiver: boolean, activityInfo?: ActivityReviewInfo) => void
  onLinkPress?: (link: string) => void
  onAttachmentPressed?: (attachment: MessageAttachmentData, item: Message) => void
  onEssentialsPress?: (info: EssentialsPressInfo) => void
  isAiCoachSelected?: boolean
  isProviderAiCoachView?: boolean
  onAiCoachFeedbackPressed?: OnAiCoachFeedbackPressedType
  hideTimestampOnHover?: boolean
  dblAttachmentsDisabled?: boolean
  showFeedbackOnHoverLeft?: boolean
}

const Row = styled.View<{ receiver: boolean; showFeedbackOnHoverLeft?: boolean }>(
  ({ receiver, showFeedbackOnHoverLeft }) => {
    const style = { marginVertical: '6px' }
    if (receiver) {
      return {
        ...style,
        flexDirection: 'row',
        flexWrap: 'nowrap',
      }
    } else {
      return {
        ...style,
        flexDirection: 'row-reverse',
        ...(showFeedbackOnHoverLeft ? { alignItems: 'center' } : {}),
      }
    }
  },
)

const StatusContainer = styled.View({
  marginTop: '5px',
  marginRight: '5px',
  alignItems: 'flex-end',
})

const FailedStatus = styled.View({
  flexDirection: 'row',
})

const WarningIconContainer = styled.View({
  marginRight: '4px',
})

const AvatarContainer = styled.View({
  marginTop: '2px',
})

const HoverFeedbackContainer = styled.View<{ isHovered: boolean }>(({ isHovered }) => ({
  marginRight: 4,
  opacity: isHovered ? 1 : 0,
  justifyContent: 'center',
  transform: isHovered ? 'translateX(0px)' : 'translateX(6px)',
  ...(IS_WEB && { transition: 'opacity 150ms ease, transform 150ms ease' }),
}))

/**
 * This component renders a single message row which consists of the avatar, time stamp of when the message was created
 * and the chat bubble which contains the message itself.
 * Component also handles updating the status delivery of a message
 */
let MessageItem: FunctionComponent<MessageItemProps> = ({
  item,
  onChatBubblePressed,
  onLinkPress,
  onAttachmentPressed,
  onEssentialsPress,
  onAiCoachFeedbackPressed,
  isAiCoachSelected,
  isProviderAiCoachView,
  hideTimestampOnHover = false,
  dblAttachmentsDisabled,
  showFeedbackOnHoverLeft = false,
}) => {
  const intl = useIntl()
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const { isMobileSized } = useMediaQuerySize()
  const {
    chatBubbleReceiver,
    chatBubbleSender,
    chatBubbleReceiverAi,
    chatBubbleSenderAi,
    icon: messageItemIcon,
  } = colors.components.messageItem
  const messageRef = useRef<HTMLElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const {
    receiver,
    dateCreated,
    avatarDetails,
    message,
    sendStatus,
    messageId,
    showTime,
    linkText,
    activityResponseId,
    activityId,
    submitDate,
    activityTitle,
    type,
    messageType,
    attachments,
    essentials,
    authorType,
    messageFeedbackValue,
    genAiMessageId,
    feedbackResponseId,
    riskLevel,
  } = item

  const formattedDate = getFriendlyFormattedTimeStamp(intl, dateCreated)
  const dateCreatedObject = new Date(dateCreated)
  const locale = Intl.DateTimeFormat().resolvedOptions().locale
  const intlFormattedDateTime = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  }).format(dateCreatedObject)
  const intlFormattedTime = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  }).format(dateCreatedObject)
  const isAICoachMessage = (isAiCoachSelected && receiver) || authorType === MESSAGE_AUTHOR_TYPE.AI_COACH

  useEffect(() => {
    if (messageRef && messageRef.current && Platform.OS === 'web' && !hideTimestampOnHover) {
      messageRef.current.title = intlFormattedDateTime
    }
  }, [intlFormattedDateTime, hideTimestampOnHover])

  // Using DOM mouseenter/mouseleave instead of Pressable.onHoverIn/Out because Pressable's
  // hover detection is pointer-event-based and breaks when nested interactive children (e.g.
  // TouchableOpacity, links) consume pointer events. mouseenter/mouseleave fire based on the
  // element's bounding box regardless of child pointer event handling.
  useEffect(() => {
    if (Platform.OS !== 'web' || !messageRef.current || !showFeedbackOnHoverLeft) return
    const el = messageRef.current
    const handleEnter = () => setIsHovered(true)
    const handleLeave = () => setIsHovered(false)
    el.addEventListener('mouseenter', handleEnter)
    el.addEventListener('mouseleave', handleLeave)
    return () => {
      el.removeEventListener('mouseenter', handleEnter)
      el.removeEventListener('mouseleave', handleLeave)
    }
  }, [showFeedbackOnHoverLeft])

  const chatBubblePosition: ViewStyle = {
    marginLeft: avatarDetails ? 15 : 0,
    // margin right below accounts for the avatar being shown next to message
    marginRight: receiver && avatarDetails ? 75 : isAICoachMessage ? 15 : 0,
    maxWidth: '75%',
  }

  // taken from ui-core. using regex to detect a url within a string
  const urlRegex = /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;$]*[-A-Z0-9+&@#/%=~_|])/gi
  const hasLinkInText = function (text: any) {
    const tokens = text.split(urlRegex)
    return tokens.length > 1
  }

  const failed = sendStatus === 'failed'
  const hasLink = hasLinkInText(message)
  // TODO: Remove the Platform Check after https://lyrahealth.atlassian.net/browse/BCDELIVERY-4707
  //  when it'll become interactive in mobile
  const isInteractive =
    Boolean(linkText) ||
    (Boolean(activityResponseId) && Boolean(activityId)) ||
    (messageType && ['session_cancellation', 'request_session_time'].includes(messageType) && Platform.OS === 'web')
  const chatBubbleTouchable =
    (!receiver && failed) ||
    (receiver && !failed && isInteractive) ||
    Boolean(activityId) ||
    (messageType && ['session_cancellation', 'request_session_time'].includes(messageType) && Platform.OS === 'web')

  // Define chat bubble colors - Aurora uses uniform transparent white overlay for all bubbles
  const auroraChatColors = {
    backgroundColor: colors.components.messageItem.chatBubbleAurora.background,
    borderColor: 'transparent',
    fontColor: colors.components.messageItem.chatBubbleAurora.text,
  }

  const chatColorConfig = {
    receiver: {
      aiCoachConversation: isProviderAiCoachView
        ? {
            backgroundColor: chatBubbleSenderAi.background,
            borderColor: chatBubbleSenderAi.border,
            fontColor: chatBubbleSenderAi.text,
          }
        : {
            backgroundColor: [
              chatBubbleReceiverAi.gradientStart.background,
              chatBubbleReceiverAi.gradientEnd.background,
            ],
            borderColor: chatBubbleReceiverAi.border,
            fontColor: chatBubbleReceiverAi.text,
          },
      providerConversation: {
        backgroundColor: chatBubbleReceiver.background,
        borderColor: chatBubbleReceiver.border,
        fontColor: colors.textPrimary,
      },
    },
    sender: {
      aiCoachConversation: isProviderAiCoachView
        ? {
            backgroundColor: [
              chatBubbleReceiverAi.gradientStart.background,
              chatBubbleReceiverAi.gradientEnd.background,
            ],
            borderColor: chatBubbleReceiverAi.border,
            fontColor: chatBubbleReceiverAi.text,
          }
        : {
            backgroundColor: chatBubbleSenderAi.background,
            borderColor: chatBubbleSenderAi.border,
            fontColor: chatBubbleSenderAi.text,
          },
      providerConversation: {
        backgroundColor: chatBubbleSender.background,
        borderColor: chatBubbleSender.border,
        fontColor: colors.textInverse,
      },
    },
  }

  const { backgroundColor, borderColor, fontColor } = isAuroraTheme
    ? auroraChatColors
    : chatColorConfig[receiver ? 'receiver' : 'sender'][
        isAiCoachSelected ? 'aiCoachConversation' : 'providerConversation'
      ]

  const onPress = () => {
    onChatBubblePressed && chatBubbleTouchable
      ? isInteractive && !hasLink
        ? onChatBubblePressed(messageId, receiver, {
            id: activityId,
            responseId: activityResponseId,
            activityType: messageType,
          })
        : onChatBubblePressed(messageId, receiver)
      : noop()
  }

  const icon =
    messageType === 'request_session_time' ? (
      <CalendarBookingIcon fillColor={!receiver ? colors.iconInverse : messageItemIcon.receiver.fill} />
    ) : (
      <ExerciseIcon fillColor={!receiver ? colors.iconInverse : messageItemIcon.receiver.fill} />
    )

  const renderEssentialsContent = (essentials: any) => {
    return essentials.map((essential: EssentialsMessageMetadata) => {
      return (
        <EssentialsBubble
          key={essential.uri}
          title={essential.title}
          url={essential.uri}
          thumbnailURI={essential.thumbnail}
          articleTypeLabel={essential.articleTypeLabel}
          readTime={essential.readTime}
          displayOnly={isProviderAiCoachView}
          onEssentialsPress={(info) => onEssentialsPress && onEssentialsPress(info)}
        />
      )
    })
  }

  const showHoverLeftFeedback =
    showFeedbackOnHoverLeft && isAICoachMessage && onAiCoachFeedbackPressed && genAiMessageId && !riskLevel
  const showInlineFeedback =
    !showFeedbackOnHoverLeft && isAICoachMessage && onAiCoachFeedbackPressed && genAiMessageId && !riskLevel

  const chatBubbleContent = (
    <TouchableOpacity onPress={onPress} disabled={!chatBubbleTouchable}>
      {type === MessageType.ACTIVITY || messageType === 'request_session_time' ? (
        <ActivityBubble
          title={activityTitle}
          backgroundColor={backgroundColor}
          activityText={message}
          submitDate={submitDate}
          fontColor={!receiver ? colors.textInverse : colors.textPrimary}
          icon={icon}
        />
      ) : IS_WEB && !isMobileSized && !hideTimestampOnHover ? (
        <Tooltip
          content={isToday(dateCreatedObject) ? intlFormattedTime : intlFormattedDateTime}
          placement='top'
          hoverEnabled
          style={{ width: '100%' }}
        >
          <ChatBubble
            body={<ChatBubbleText text={decodeHtmlEntities(message)} fontColor={fontColor} onLinkPress={onLinkPress} />}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            messageType={messageType}
          />
        </Tooltip>
      ) : (
        <ChatBubble
          body={<ChatBubbleText text={decodeHtmlEntities(message)} fontColor={fontColor} onLinkPress={onLinkPress} />}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          messageType={messageType}
        />
      )}
      {essentials?.length ? renderEssentialsContent(essentials) : null}
      {showInlineFeedback && (
        <MessageFeedback
          onPress={(value) => onAiCoachFeedbackPressed({ value, genAiMessageId, feedbackResponseId })}
          value={messageFeedbackValue}
        />
      )}
    </TouchableOpacity>
  )

  return (
    // casting to unknown because TS says View and HTMLElements do not have enough overlap
    //   if we don't cast to unknown, we'll need a tsignore when we set the ref's index property
    <View testID={tID('MessageItem')} ref={messageRef as unknown as React.LegacyRef<View>}>
      {showTime ? (
        <View testID={tID('MessageItem-timestamp')}>
          <Timestamp time={formattedDate} />
        </View>
      ) : null}
      {type === MessageType.DELETED ? (
        <DeletedLiveMessageText />
      ) : (
        <Row receiver={receiver} showFeedbackOnHoverLeft={showFeedbackOnHoverLeft}>
          {avatarDetails && (
            <AvatarContainer testID={tID('Messages-avatar')}>
              {isAICoachMessage && isAuroraTheme ? (
                <AuroraAICoachAvatarIcon size={48} />
              ) : (
                <Avatar
                  details={avatarDetails}
                  accessibilityLabel={intl.formatMessage(avatarAltTextGeneric, { name: avatarDetails.displayName })}
                  size={32}
                />
              )}
            </AvatarContainer>
          )}
          <View style={chatBubblePosition}>{chatBubbleContent}</View>
          {showHoverLeftFeedback && (
            <HoverFeedbackContainer isHovered={isHovered} testID={tID('MessageFeedback-hoverContainer')}>
              <MessageFeedback
                onPress={(value) =>
                  onAiCoachFeedbackPressed({ value, genAiMessageId: genAiMessageId!, feedbackResponseId })
                }
                value={messageFeedbackValue}
                vertical
              />
            </HoverFeedbackContainer>
          )}
        </Row>
      )}
      {attachments?.map((attachment) => (
        <Row key={attachment.key} receiver={receiver}>
          <View style={chatBubblePosition} testID={tID(`MessageItem-attachment-${attachment.key}`)}>
            <MessageAttachmentRenderer
              attachment={attachment}
              onPress={() => {
                onAttachmentPressed && onAttachmentPressed(attachment, item)
              }}
              dblAttachmentsDisabled={dblAttachmentsDisabled}
            />
          </View>
        </Row>
      ))}
      {sendStatus !== 'sent' ? (
        <StatusContainer testID={tID('MessageItem-status')}>
          {!receiver && sendStatus === 'sending' ? (
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='SENDING...'
                  description='This is a label that tells the user this message in the process of being sent'
                />
              }
              size={Size.CAPTION}
              textAlign='right'
              color={colors.textSecondary}
            />
          ) : null}
          {!receiver && sendStatus === 'failed' ? (
            <FailedStatus>
              <WarningIconContainer>
                <WarningIcon size={16} fillColor={colors.iconError} />
              </WarningIconContainer>
              <BodyText
                text={
                  <FormattedMessage
                    defaultMessage='NOT DELIVERED'
                    description='This is a label that tells the user this message did not reach the server and was not delivered'
                  />
                }
                size={Size.CAPTION}
                textAlign='right'
                color={colors.textError}
              />
            </FailedStatus>
          ) : null}
        </StatusContainer>
      ) : null}
    </View>
  )
}

MessageItem = React.memo(MessageItem)

export { MessageItem }
